// src/hooks/useMobileMenu.ts
import { useEffect } from "react";

type MobileMenuOpts = {
  cmnNavSelector?: string;           // ".cmn-nav"
  desktopMenuSelector?: string;      // ".navbar__menu"
  desktopOptionsSelector?: string;   // ".navbar__mobile-options"
  mobileMenuSelector?: string;       // ".mobile-menu"
  mobileWrapperSelector?: string;    // ".mobile-menu__wrapper"
  mobileListSelector?: string;       // ".mobile-menu__list"
  mobileOptionsSelector?: string;    // ".mobile-menu__options"
  backdropSelector?: string;         // ".mobile-menu__backdrop"
  openBtnSelector?: string;          // ".open-mobile-menu, .open-offcanvas-nav"
  closeBtnSelector?: string;         // ".close-mobile-menu, .mobile-menu__backdrop"
  dropdownLabelSelector?: string;    // ".navbar__dropdown-label"
  subMenuSelector?: string;          // ".navbar__sub-menu"
  navFadeSelector?: string;          // ".nav-fade"
  showClass?: string;                // "show-menu"
  backdropActiveClass?: string;      // "mobile-menu__backdrop-active"
  navFadeActiveClass?: string;       // "nav-fade-active"
  itemActiveClass?: string;          // "navbar__item-active"
  cloneDesktopIntoMobile?: boolean;  // true
};

export function useMobileMenu(opts: MobileMenuOpts = {}) {
  const {
    cmnNavSelector = ".cmn-nav",
    desktopMenuSelector = ".navbar__menu",
    desktopOptionsSelector = ".navbar__mobile-options",
    mobileMenuSelector = ".mobile-menu",
    mobileWrapperSelector = ".mobile-menu__wrapper",
    mobileListSelector = ".mobile-menu__list",
    mobileOptionsSelector = ".mobile-menu__options",
    backdropSelector = ".mobile-menu__backdrop",
    openBtnSelector = ".open-mobile-menu, .open-offcanvas-nav",
    closeBtnSelector = ".close-mobile-menu, .mobile-menu__backdrop",
    dropdownLabelSelector = ".navbar__dropdown-label",
    subMenuSelector = ".navbar__sub-menu",
    navFadeSelector = ".nav-fade",
    showClass = "show-menu",
    backdropActiveClass = "mobile-menu__backdrop-active",
    navFadeActiveClass = "nav-fade-active",
    itemActiveClass = "navbar__item-active",
    cloneDesktopIntoMobile = true,
  } = opts;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const cmnNav = document.querySelector(cmnNavSelector);
    const desktopMenu = cmnNav?.querySelector(desktopMenuSelector);
    const desktopOptions = cmnNav?.querySelector(desktopOptionsSelector);
    const mobileMenu = document.querySelector(mobileMenuSelector);
    const mobileWrapper = document.querySelector(mobileWrapperSelector);
    const mobileList = document.querySelector(mobileListSelector);
    const mobileOptions = document.querySelector(mobileOptionsSelector);
    const backdrop = document.querySelector(backdropSelector);

    if (!mobileMenu || !mobileWrapper || !mobileList) return;

    // helper: hide all submenus
    const hideAllSubmenus = () => {
      mobileMenu.querySelectorAll<HTMLElement>(subMenuSelector).forEach((ul) => {
        ul.style.display = "none";
      });
      mobileMenu.querySelectorAll<HTMLElement>(dropdownLabelSelector).forEach((l) => {
        l.classList.remove(itemActiveClass);
        l.setAttribute("aria-expanded", "false");
      });
    };

    // 1) Clone desktop items into mobile containers
    if (cloneDesktopIntoMobile && desktopMenu) {
      mobileList.innerHTML = desktopMenu.innerHTML;
    }
    if (cloneDesktopIntoMobile && desktopOptions && mobileOptions) {
      mobileOptions.innerHTML = desktopOptions.innerHTML;
    }

    // ensure initial hidden state (even if not cloning)
    hideAllSubmenus();

    // 2) Dropdown toggle behavior (event delegation inside mobile menu only)
    const onDropdownClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!mobileMenu.contains(target)) return;

      const label = target.closest(dropdownLabelSelector) as HTMLElement | null;
      if (!label) return;

      // prevent default if anchor
      if (label.tagName === "A") {
        e.preventDefault();
      }

      const parentLi = label.parentElement as HTMLElement;
      const currentSub = parentLi?.querySelector<HTMLElement>(subMenuSelector);
      if (!currentSub) return;

      // close others
      mobileMenu.querySelectorAll<HTMLElement>(subMenuSelector).forEach((ul) => {
        if (ul !== currentSub) ul.style.display = "none";
      });
      mobileMenu.querySelectorAll<HTMLElement>(dropdownLabelSelector).forEach((l) => {
        if (l !== label) {
          l.classList.remove(itemActiveClass);
          l.setAttribute("aria-expanded", "false");
        }
      });

      // toggle current
      const isOpen = currentSub.style.display === "block";
      currentSub.style.display = isOpen ? "none" : "block";
      label.classList.toggle(itemActiveClass, !isOpen);
      label.setAttribute("aria-expanded", (!isOpen).toString());
    };

    mobileMenu.addEventListener("click", onDropdownClick);

    // 3) Open / Close handlers
    const open = () => {
      document.querySelectorAll<HTMLElement>(navFadeSelector).forEach((el, i) => {
        el.style.animationDelay = `${0.2 * i}s`;
      });
      backdrop?.classList.add(backdropActiveClass);
      mobileMenu.classList.add(showClass);
      mobileWrapper.classList.remove(navFadeActiveClass);
      document.body.classList.add("body-active");
    };

    const close = () => {
      setTimeout(() => {
        mobileMenu.classList.remove(showClass);
      }, 900);
      setTimeout(() => {
        backdrop?.classList.remove(backdropActiveClass);
      }, 1100);
      mobileWrapper.classList.add(navFadeActiveClass);
      document.body.classList.remove("body-active");
      // collapse all submenus when closing
      hideAllSubmenus();
    };

    const handleOpenClick = (e: Event) => {
      const t = e.target as HTMLElement;
      const btn = t.closest(openBtnSelector);
      if (btn) {
        e.preventDefault();
        open();
      }
    };
    const handleCloseClick = (e: Event) => {
      const t = e.target as HTMLElement;
      const btn = t.closest(closeBtnSelector);
      if (btn) {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener("click", handleOpenClick);
    document.addEventListener("click", handleCloseClick);

    // 4) Reset on resize
    const onResize = () => {
      document.body.classList.remove("body-active");
      mobileMenu.classList.remove(showClass);
      backdrop?.classList.remove(backdropActiveClass);
      mobileWrapper.classList.add(navFadeActiveClass);
      hideAllSubmenus();
    };
    window.addEventListener("resize", onResize);

    return () => {
      mobileMenu.removeEventListener("click", onDropdownClick);
      document.removeEventListener("click", handleOpenClick);
      document.removeEventListener("click", handleCloseClick);
      window.removeEventListener("resize", onResize);
    };
  }, [
    cmnNavSelector,
    desktopMenuSelector,
    desktopOptionsSelector,
    mobileMenuSelector,
    mobileWrapperSelector,
    mobileListSelector,
    mobileOptionsSelector,
    backdropSelector,
    openBtnSelector,
    closeBtnSelector,
    dropdownLabelSelector,
    subMenuSelector,
    navFadeSelector,
    showClass,
    backdropActiveClass,
    navFadeActiveClass,
    itemActiveClass,
    cloneDesktopIntoMobile,
  ]);
}
